/**
 * Detector performance counters and patient-specific seizure template matching.
 */

import type {
  AITriageCandidateCategory,
  AITriageDispositionStatus,
} from "./aiTriageTypes";

export const DETECTOR_PERF_SCHEMA_VERSION = "detector-perf-v1.0.0";
export const SEIZURE_TEMPLATE_SCHEMA_VERSION = "seizure-template-v1.0.0";

export type DetectorDispositionCount = {
  confirmed: number;
  rejected: number;
  falsePositive: number;
  pending: number;
};

export type DetectorPerformanceCounters = {
  schemaVersion: string;
  windowHours: number;
  byCategory: Partial<Record<AITriageCandidateCategory, DetectorDispositionCount>>;
  totalCandidates: number;
  falsePositivesPer24h: number;
  confirmationRate: number;
};

export type DetectorCandidateInput = {
  category: AITriageCandidateCategory;
  status: AITriageDispositionStatus;
  detectedAtIso?: string | null;
};

export function emptyDispositionCount(): DetectorDispositionCount {
  return { confirmed: 0, rejected: 0, falsePositive: 0, pending: 0 };
}

export function aggregateDetectorPerformance(
  candidates: DetectorCandidateInput[],
  windowHours = 24,
): DetectorPerformanceCounters {
  const byCategory: DetectorPerformanceCounters["byCategory"] = {};
  let confirmed = 0;
  let falsePositive = 0;
  let decided = 0;

  for (const candidate of candidates) {
    const bucket =
      byCategory[candidate.category] || emptyDispositionCount();
    if (candidate.status === "confirmed") {
      bucket.confirmed += 1;
      confirmed += 1;
      decided += 1;
    } else if (candidate.status === "rejected") {
      bucket.rejected += 1;
      decided += 1;
    } else if (candidate.status === "false_positive") {
      bucket.falsePositive += 1;
      falsePositive += 1;
      decided += 1;
    } else {
      bucket.pending += 1;
    }
    byCategory[candidate.category] = bucket;
  }

  const scale = 24 / Math.max(1, windowHours);
  return {
    schemaVersion: DETECTOR_PERF_SCHEMA_VERSION,
    windowHours,
    byCategory,
    totalCandidates: candidates.length,
    falsePositivesPer24h: falsePositive * scale,
    confirmationRate: decided > 0 ? confirmed / decided : 0,
  };
}

export type PatientSeizureTemplate = {
  templateId: string;
  patientId: string;
  admissionId: string;
  label: string;
  channelSet: string[];
  startTimeSeconds: number;
  endTimeSeconds: number;
  morphologyTags: string[];
  confirmedBy: string;
  confirmedAtIso: string;
  schemaVersion: string;
};

export function createPatientSeizureTemplate(input: {
  templateId: string;
  patientId: string;
  admissionId: string;
  label: string;
  channelSet?: string[];
  startTimeSeconds: number;
  endTimeSeconds: number;
  morphologyTags?: string[];
  confirmedBy: string;
  confirmedAt?: Date;
}): PatientSeizureTemplate {
  const start = Math.max(0, input.startTimeSeconds);
  const end = Math.max(start, input.endTimeSeconds);
  return {
    templateId: input.templateId,
    patientId: input.patientId,
    admissionId: input.admissionId,
    label: input.label.trim() || "Confirmed seizure",
    channelSet: input.channelSet || [],
    startTimeSeconds: start,
    endTimeSeconds: end,
    morphologyTags: input.morphologyTags || [],
    confirmedBy: input.confirmedBy,
    confirmedAtIso: (input.confirmedAt || new Date()).toISOString(),
    schemaVersion: SEIZURE_TEMPLATE_SCHEMA_VERSION,
  };
}

export type TemplateMatchScore = {
  templateId: string;
  score: number;
  matched: boolean;
  reasons: string[];
};

/**
 * Simple admission-scoped template match using channel overlap and
 * duration similarity. Not a diagnostic claim — screening assist only.
 */
export function matchCandidateToSeizureTemplates(input: {
  channelSet: string[];
  durationSeconds: number;
  templates: PatientSeizureTemplate[];
  minScore?: number;
}): TemplateMatchScore[] {
  const minScore = input.minScore ?? 0.55;
  const candidateChannels = new Set(input.channelSet.map((c) => c.toUpperCase()));
  return input.templates.map((template) => {
    const reasons: string[] = [];
    const templateChannels = template.channelSet.map((c) => c.toUpperCase());
    const overlap = templateChannels.filter((c) => candidateChannels.has(c));
    const channelScore =
      templateChannels.length === 0
        ? 0.4
        : overlap.length / templateChannels.length;
    if (channelScore >= 0.5) reasons.push("channel overlap");

    const templateDuration = Math.max(
      0.1,
      template.endTimeSeconds - template.startTimeSeconds,
    );
    const durationRatio =
      Math.min(input.durationSeconds, templateDuration) /
      Math.max(input.durationSeconds, templateDuration);
    if (durationRatio >= 0.5) reasons.push("duration similarity");

    const score = Math.max(0, Math.min(1, channelScore * 0.6 + durationRatio * 0.4));
    return {
      templateId: template.templateId,
      score,
      matched: score >= minScore,
      reasons,
    };
  });
}

export function tuneScreeningThresholdWithTemplates(input: {
  baseThreshold: number;
  matches: TemplateMatchScore[];
}): number {
  const best = input.matches
    .filter((m) => m.matched)
    .sort((a, b) => b.score - a.score)[0];
  if (!best) return input.baseThreshold;
  // Lower threshold slightly when a confirmed patient template matches.
  return Math.max(0.05, input.baseThreshold - best.score * 0.15);
}
